import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Search, FileDown, Package } from "lucide-react";
import Seo from "@/components/Seo";
import { Footer } from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
    paid: "border-emerald-800/60 bg-emerald-950/40 text-emerald-400",
    shipped: "border-amber-800/60 bg-amber-950/40 text-amber-400",
    delivered: "border-bone/40 bg-bone/10 text-bone",
    cancelled: "border-red-800/60 bg-red-950/40 text-red-500",
};

export default function TrackOrder() {
    const [email, setEmail] = useState("");
    const [orders, setOrders] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const lookup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/orders/lookup`, { email });
            setOrders(data);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "Lookup failed — try again.");
            setOrders(null);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen pt-28" data-testid="track-order-page">
            <Seo
                title="Track Your Order — Logan's Reptiles & Exotics"
                siteName="Logan's Reptiles & Exotics"
                description="Look up your order status and re-download your species care sheets with the email you used at checkout."
            />
            <div className="mx-auto max-w-3xl px-6 sm:px-12">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="font-mono2 text-xs uppercase tracking-[0.35em] text-red-500"
                >
                    Guest Order Tracking
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.1 }}
                    className="mt-4 font-display text-4xl font-extrabold tracking-tight text-bone sm:text-5xl"
                >
                    Find your <span className="font-editorial italic text-red-500">order</span>
                </motion.h1>
                <p className="mt-5 max-w-lg text-sm leading-relaxed text-ash">
                    Enter the email you used at checkout. You'll see every order, its status, and fresh
                    download links for your species care sheets.
                </p>

                <motion.form
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2 }}
                    onSubmit={lookup}
                    className="mt-10 flex gap-3"
                    data-testid="track-order-form"
                >
                    <input
                        data-testid="track-email-input"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com"
                        className="flex-1 border border-red-950/60 bg-black/50 px-5 py-4 text-sm text-bone placeholder:text-ash/40 outline-none transition-colors duration-300 focus:border-red-700"
                    />
                    <button
                        data-testid="track-lookup-button"
                        type="submit"
                        disabled={loading}
                        className="flex items-center gap-2 bg-crimson px-7 py-4 font-mono2 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-red-700 disabled:opacity-50"
                    >
                        <Search className="h-4 w-4" /> {loading ? "Searching…" : "Look Up"}
                    </button>
                </motion.form>

                {error && (
                    <p data-testid="track-error" className="mt-5 border border-red-800/60 bg-red-950/30 p-3 text-xs text-red-400">
                        {error}
                    </p>
                )}

                {orders !== null && (
                    <div className="mt-10 pb-24" data-testid="track-results">
                        {orders.length === 0 ? (
                            <p className="border border-red-950/50 bg-coal p-8 text-center font-editorial text-lg italic text-ash" data-testid="track-empty">
                                No orders found for that email. Check the spelling — or go adopt something wild.
                            </p>
                        ) : (
                            orders.map((o) => (
                                <motion.div
                                    key={o.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="mb-5 border border-red-950/50 bg-coal p-6"
                                    data-testid="track-order-card"
                                >
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <p className="flex items-center gap-2 font-mono2 text-xs uppercase tracking-[0.2em] text-ash">
                                            <Package className="h-4 w-4 text-red-500" />
                                            {new Date(o.created_at).toLocaleDateString()}
                                        </p>
                                        <span
                                            data-testid="track-order-status"
                                            className={`border px-2 py-1 font-mono2 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[o.status] || STATUS_STYLE.paid}`}
                                        >
                                            {o.status}
                                        </span>
                                    </div>
                                    <ul className="mt-4 space-y-1.5 text-sm text-ash">
                                        {o.items.map((i, idx) => (
                                            <li key={idx} className="flex justify-between">
                                                <span>{i.quantity}× {i.name}</span>
                                                <span className="font-mono2 text-bone">
                                                    ${(i.unit_price * i.quantity).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                    <p className="mt-4 border-t border-red-950/40 pt-3 text-right font-mono2 text-sm text-bone">
                                        Total · ${Number(o.amount_total).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </p>
                                    {o.care_sheets.length > 0 && (
                                        <div className="mt-4" data-testid="track-care-sheets">
                                            <p className="font-mono2 text-[9px] uppercase tracking-[0.25em] text-red-500">
                                                Care sheet downloads
                                            </p>
                                            {o.care_sheets.map((s) => (
                                                <a
                                                    key={s.product_id}
                                                    data-testid="track-care-sheet-link"
                                                    href={`${API}${s.url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-2 flex items-center gap-2 border border-red-900/50 px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.15em] text-bone transition-colors duration-300 hover:bg-crimson"
                                                >
                                                    <FileDown className="h-3.5 w-3.5 text-red-500" /> {s.name}
                                                </a>
                                            ))}
                                        </div>
                                    )}
                                </motion.div>
                            ))
                        )}
                    </div>
                )}
            </div>
            <Footer />
        </main>
    );
}
